import {
  ADMITTED_FROM_GROUPED,
  CUSTOMER_DISCHARGE_REASON_GROUPED,
  CUSTOMER_TYPE,
  CUSTOMER_TYPE_TEXT,
  DAYS_OF_WEEK_OPTIONS,
  DIET_TEXTURE,
  DIET_TEXTURE_TEXT,
  DIETARY_RESTRICTIONS_GROUPED,
  EMERGENCY_CONTACT_RELATIONSHIP,
  EMERGENCY_CONTACT_RELATIONSHIP_TEXT,
  EMERGENCY_CONTACT_ROLE_OPTIONS,
  IMMIGRATION_STATUS,
  IMMIGRATION_STATUS_TEXT,
  LANGUAGE_OPTIONS,
  LEGAL_SEX,
  LEGAL_SEX_TEXT,
  MARITAL_STATUS,
  MARITAL_STATUS_TEXT,
  MEAL_TYPE,
  MEAL_TYPE_TEXT,
  PAY_SOURCE,
  PAY_SOURCE_TEXT,
  PREFERRED_TEXT_LANGUAGE,
  PREFERRED_TEXT_LANGUAGE_TEXT,
  PROGRAM_TYPE,
  PROGRAM_TYPE_TEXT,
  REFERRAL_SOURCE_GROUPED,
  SOCIAL_BENEFIT_FIELD_KEYS,
  STATE_OPTIONS,
  STATE_OPTIONS_TEXT,
  TRANSPORTATION_TYPE,
  TRANSPORTATION_TYPE_TEXT,
  YES_NO,
  YES_NO_TEXT,
  PRE_SCREENING_FORM,
  PRE_SCREENING_FORM_TEXT,
} from "../../shared";

const mapOptions = (valueMap, textMap) =>
  Object.values(valueMap).map((value) => ({
    value,
    label: textMap[value] || value,
  }));

const yesNoOptions = mapOptions(YES_NO, YES_NO_TEXT);
const preScreeningOptions = mapOptions(PRE_SCREENING_FORM, PRE_SCREENING_FORM_TEXT);

const ADMISSION_RECORD_KEYS = new Set([
  "admission_date",
  "admission_number",
  "admitted_from",
  "enrolled_date",
  "referral_source",
]);

export const BULK_EDIT_TAB_LABELS = {
  personalInfo: "Personal Information",
  careServices: "Care & Services",
  medicalInsurance: "Medical & Insurance",
  confidentialDetails: "Confidential Details",
};

/** Same sections and field labels as Update Customer, minus auto-generated and file-only fields. */
const FIELD_SECTIONS = [
  {
    tab: "personalInfo",
    group: "Basic Info",
    fields: [
      { key: "firstname", label: "First Name", input: "text" },
      { key: "middle_name", label: "Middle Name", input: "text" },
      { key: "lastname", label: "Last Name", input: "text" },
      { key: "name_cn", label: "Chinese / Preferred Name", input: "text" },
      { key: "name_on_id", label: "Name On ID", input: "text" },
      {
        key: "type",
        label: "Customer Type",
        input: "select",
        required: true,
        options: [CUSTOMER_TYPE.MEMBER, CUSTOMER_TYPE.VISITOR].map((value) => ({
          value,
          label: CUSTOMER_TYPE_TEXT[value],
        })),
      },
      {
        key: "legal_sex",
        label: "Legal Sex",
        input: "select",
        required: true,
        options: mapOptions(LEGAL_SEX, LEGAL_SEX_TEXT),
      },
      {
        key: "birth_date",
        label: "Date Of Birth",
        input: "date",
      },
      {
        key: "marriage_date",
        label: "Marriage Date",
        input: "date",
      },
      {
        key: "language_spoken",
        label: "Language Spoken",
        input: "multiselect",
        options: LANGUAGE_OPTIONS,
        otherKey: "language_spoken_other",
      },
      {
        key: "program_type",
        label: "Program Type",
        input: "select",
        required: true,
        options: mapOptions(PROGRAM_TYPE, PROGRAM_TYPE_TEXT),
      },
      {
        key: "pay_source",
        label: "Pay Source",
        input: "select",
        required: true,
        options: mapOptions(PAY_SOURCE, PAY_SOURCE_TEXT),
        otherKey: "pay_source_other",
      },
      {
        key: "marital_status",
        label: "Marital Status",
        input: "select",
        options: mapOptions(MARITAL_STATUS, MARITAL_STATUS_TEXT),
      },
      {
        key: "spouse_name",
        label: "Spouse Name",
        input: "text",
      },
      {
        key: "immigration_status",
        label: "Immigration Status",
        input: "select",
        options: mapOptions(IMMIGRATION_STATUS, IMMIGRATION_STATUS_TEXT),
        otherKey: "immigration_status_other",
      },
    ],
  },
  {
    tab: "personalInfo",
    group: "Contact Info",
    fields: [
      { key: "phone", label: "Primary Phone Number", input: "text" },
      { key: "mobile_phone", label: "Phone Number 2", input: "text" },
      { key: "home_phone", label: "Phone Number 3", input: "text" },
      { key: "email", label: "Email", input: "text" },
    ],
  },
  {
    tab: "personalInfo",
    group: "Address",
    fields: [
      { key: "address_line_1", label: "Address Line 1", input: "text" },
      { key: "address_line_2", label: "Address Line 2", input: "text" },
      { key: "city", label: "City", input: "text" },
      {
        key: "state",
        label: "State",
        input: "select",
        options: mapOptions(STATE_OPTIONS, STATE_OPTIONS_TEXT),
      },
      { key: "zip_code", label: "Zip Code", input: "text" },
      { key: "address_note", label: "Address Note", input: "text" },
    ],
  },
  {
    tab: "personalInfo",
    group: "Emergency Contact",
    fields: [
      { key: "emergency_contact_name", label: "Emergency Contact Name", input: "text" },
      { key: "emergency_contact_phone", label: "Emergency Contact Phone Number", input: "text" },
      {
        key: "emergency_contact_relationship",
        label: "Emergency Contact Relationship",
        input: "select",
        options: mapOptions(EMERGENCY_CONTACT_RELATIONSHIP, EMERGENCY_CONTACT_RELATIONSHIP_TEXT),
        otherKey: "emergency_contact_relationship_other",
      },
      {
        key: "emergency_contact_role",
        label: "Emergency Contact Role",
        input: "multiselect",
        options: EMERGENCY_CONTACT_ROLE_OPTIONS,
      },
    ],
  },
  {
    tab: "personalInfo",
    group: "Schedule",
    fields: [
      {
        key: "days_of_week",
        label: "Days of Week",
        input: "multiselect",
        options: DAYS_OF_WEEK_OPTIONS,
      },
    ],
  },
  {
    tab: "personalInfo",
    group: "Admission & Discharge Record",
    fields: [
      { key: "admission_date", label: "Admission Date", input: "date", syncAdmissionRecord: true },
      { key: "admission_number", label: "Admission Number", input: "text", syncAdmissionRecord: true },
      {
        key: "admitted_from",
        label: "Admitted From",
        input: "select",
        groupedOptions: ADMITTED_FROM_GROUPED,
        otherKey: "admitted_from_other",
        syncAdmissionRecord: true,
      },
      { key: "enrolled_date", label: "Enrolled Date", input: "date", syncAdmissionRecord: true },
      {
        key: "referral_source",
        label: "Referral Source",
        input: "select",
        groupedOptions: REFERRAL_SOURCE_GROUPED,
        otherKey: "referral_source_other",
        syncAdmissionRecord: true,
      },
      { key: "discharge_date", label: "Discharge Date", input: "date" },
      {
        key: "discharge_reason",
        label: "Discharge Reason",
        input: "select",
        groupedOptions: CUSTOMER_DISCHARGE_REASON_GROUPED,
        otherKey: "discharge_reason_other",
      },
    ],
  },
  {
    tab: "personalInfo",
    group: "Additional Information",
    fields: [
      { key: "note", label: "Notes And Attachments", input: "textarea" },
      { key: "notes_for_driver", label: "Notes for Driver", input: "textarea" },
    ],
  },
  {
    tab: "careServices",
    group: "Care & Services",
    fields: [
      { key: "food_allergies", label: "Food Allergies", input: "text" },
      {
        key: "dietary_restrictions",
        label: "Diet Preferences",
        input: "multiselect",
        groupedOptions: DIETARY_RESTRICTIONS_GROUPED,
        exclusiveOptionValue: "regular",
        otherKey: "dietary_restrictions_other",
      },
      {
        key: "diet_texture",
        label: "Diet Texture",
        input: "select",
        options: mapOptions(DIET_TEXTURE, DIET_TEXTURE_TEXT),
      },
      {
        key: "meal_type",
        label: "Meal Type",
        input: "select",
        options: mapOptions(MEAL_TYPE, MEAL_TYPE_TEXT),
      },
      {
        key: "transportation_type",
        label: "Transportation Type",
        input: "select",
        options: mapOptions(TRANSPORTATION_TYPE, TRANSPORTATION_TYPE_TEXT),
      },
      {
        key: "wheelchair",
        label: "Lift-Equipped Vehicle Required",
        input: "select",
        options: yesNoOptions,
      },
      {
        key: "consent_to_text_messages",
        label: "Consent To Text Messages",
        input: "select",
        options: yesNoOptions,
      },
      {
        key: "preferred_text_language",
        label: "Preferred Text Language",
        input: "select",
        options: mapOptions(PREFERRED_TEXT_LANGUAGE, PREFERRED_TEXT_LANGUAGE_TEXT),
      },
      {
        key: "consent_to_media_use",
        label: "Consent to Media Use",
        input: "select",
        options: yesNoOptions,
      },
    ],
  },
  {
    tab: "medicalInsurance",
    group: "Providers",
    fields: [
      {
        key: "primary_care_physician",
        label: "Primary Care Physician",
        input: "select",
        resource: "pcp",
        idKey: "primary_care_physician_id",
      },
      {
        key: "pharmacy",
        label: "Pharmacy Name",
        input: "select",
        resource: "pharmacy",
        idKey: "pharmacy_id",
      },
      { key: "pharmacy_id", label: "Pharmacy ID", input: "text" },
    ],
  },
  {
    tab: "medicalInsurance",
    group: "General Conditions",
    fields: [
      { key: "diabetes_mellitus", label: "Diabetes Mellitus", input: "select", options: yesNoOptions },
      { key: "eyes_on", label: "Eyes-On", input: "select", options: yesNoOptions },
      { key: "eyes_on_reason", label: "Eyes-On Reason", input: "text" },
      { key: "has_airtag", label: "Has Airtag", input: "select", options: yesNoOptions },
      { key: "weight", label: "Weight", input: "text", placeholder: "Enter weight (lb)" },
    ],
  },
  {
    tab: "medicalInsurance",
    group: "TB & Pre Screening",
    fields: [
      { key: "tb_screening_yes_no", label: "TB Cleared", input: "select", options: yesNoOptions },
      { key: "pre_screening_yes_no", label: "Pre-Screening Form", input: "select", options: preScreeningOptions },
      { key: "forty_five_day_admission_note", label: "45 Days Admission Note", input: "select", options: yesNoOptions },
      { key: "tb_notes", label: "TB Notes", input: "text" },
    ],
  },
  {
    tab: "medicalInsurance",
    group: "Legal",
    fields: [
      { key: "molst", label: "MOLST", input: "text" },
      { key: "provisions_for_advance_medical", label: "Advance Directive /Living Will", input: "text" },
      { key: "hospice", label: "Hospice", input: "text" },
      { key: "burial_arrangements", label: "CPR", input: "select", options: yesNoOptions },
      { key: "power_of_attorney", label: "Power of Attorney", input: "text" },
    ],
  },
  {
    tab: "medicalInsurance",
    group: "Medication Management",
    fields: [
      {
        key: "medications_given_at_center",
        label: "Medications given at the center",
        input: "select",
        options: yesNoOptions,
      },
      { key: "pillbox", label: "Pillbox", input: "select", options: yesNoOptions },
      { key: "medication_note", label: "Medication Note", input: "text" },
      { key: "medication_type", label: "Medication Type", input: "text", placeholder: "Enter medication type" },
      { key: "pillbox_type", label: "Pillbox Type", input: "text", placeholder: "Enter pillbox type" },
      { key: "pillbox_note", label: "Pillbox Note", input: "text", placeholder: "Enter pillbox note" },
    ],
  },
  {
    tab: "medicalInsurance",
    group: "Rounding",
    fields: [
      { key: "requires_rounding", label: "Requires Rounding", input: "select", options: yesNoOptions },
      { key: "rounding_notes", label: "Rounding Notes", input: "text" },
    ],
  },
  {
    tab: "medicalInsurance",
    group: "Compliance & Deadlines",
    fields: [
      { key: "adcaps_completed_date", label: "Adcaps Due Date", input: "date" },
      { key: "adcaps_note", label: "ADCAPS Note", input: "text" },
      { key: "ltss_id_number", label: "LTSS ID Number", input: "text" },
      { key: "medicaid_expiration_date", label: "Medicaid Expiration Date", input: "date" },
      { key: "va_expiration_date", label: "VA Expiration Date", input: "date" },
      { key: "medicaid_renewal", label: "Medicaid Renewal", input: "select", options: yesNoOptions },
      { key: "medicaid_renew_date", label: "Medicaid Renew Date", input: "date" },
      { key: "id_expiration_date", label: "ID Expiration Date", input: "date" },
    ],
  },
  {
    tab: "confidentialDetails",
    group: "Social Benefits",
    fields: SOCIAL_BENEFIT_FIELD_KEYS.map(({ key, label }) => ({
      key,
      label,
      input: "select",
      options: yesNoOptions,
    })),
  },
  {
    tab: "confidentialDetails",
    group: "Confidential Details",
    fields: [
      { key: "medicare_number", label: "Medicare Number", input: "text" },
      { key: "medicaid_number", label: "Medicaid Number", input: "text" },
      { key: "social_security_number", label: "Social Security Number", input: "text" },
    ],
  },
];

export const getBulkEditFieldSections = (canEditTab) =>
  FIELD_SECTIONS.map((section) => ({
    ...section,
    fields: section.fields
      .filter(() => (typeof canEditTab === "function" ? canEditTab(section.tab) : true))
      .map((field) => ({ ...field, tab: section.tab })),
  })).filter((section) => section.fields.length > 0);

export const persistKeysForBulkEditField = (field) => {
  if (!field?.key) return [];
  const keys = [field.key];
  if (field.otherKey) keys.push(field.otherKey);
  if (field.idKey) keys.push(field.idKey);
  if (field.key === "legal_sex") keys.push("gender");
  if (field.key === "primary_care_physician") keys.push("care_provider");
  if (field.key === "firstname" || field.key === "lastname") keys.push("name");
  if (field.key === "language_spoken") keys.push("language");
  if (field.key === "consent_to_text_messages") keys.push("text_msg_enabled");
  if (field.key === "eyes_on") keys.push("disability");
  if (
    field.key === "emergency_contact_name" ||
    field.key === "emergency_contact_phone" ||
    field.key === "emergency_contact_relationship"
  ) {
    keys.push("emergency_contact");
  }
  if (ADMISSION_RECORD_KEYS.has(field.key) || field.syncAdmissionRecord) {
    keys.push("admission_records");
  }
  return keys;
};

export const findBulkEditField = (key, sections = FIELD_SECTIONS) => {
  for (const section of sections) {
    const field = section.fields.find((item) => item.key === key);
    if (field) return { ...field, tab: field.tab || section.tab };
  }
  return null;
};
